import React, { useState } from "react";
import {
    CheckCircle,
    AlertTriangle,
    SkipForward,
    XCircle,
    ChevronDown,
    ChevronRight,
    Clock,
    Calendar,
    Layers,
    TrendingUp,
} from "lucide-react";
import styles from "./RbExperienceAnalyser.module.css";

const SECTIONS = [
    {
        key: "counted",
        label: "Counted",
        icon: CheckCircle,
        colorVar: "--status-counted",
        description: "Ranges that contributed to total experience",
    },
    {
        key: "corrected",
        label: "Corrected",
        icon: AlertTriangle,
        colorVar: "--status-corrected",
        description: "Had issues but recovered and counted",
    },
    {
        key: "skipped",
        label: "Skipped",
        icon: SkipForward,
        colorVar: "--status-skipped",
        description: "Valid but uncountable — exit point unknown",
    },
    {
        key: "corrupted",
        label: "Corrupted",
        icon: XCircle,
        colorVar: "--status-corrupted",
        description: "Bad data — unusable records",
    },
];

function parseTag(record) {
    // "[start: X | end: Y | current: Z | designation: A | salon: B]"
    const match = record.match(
        /\[start:\s*(.+?)\s*\|\s*end:\s*(.+?)\s*\|\s*current:\s*(.+?)\s*\|\s*designation:\s*(.+?)\s*\|\s*salon:\s*(.+?)\]/
    );
    if (!match) return { raw: record };
    return {
        start: match[1],
        end: match[2],
        current: match[3],
        designation: match[4],
        salon: match[5],
    };
}

function RecordTag({ record }) {
    const parsed = parseTag(record);
    if (parsed.raw) return <span className={styles.rawTag}>{parsed.raw}</span>;

    const isValid = parsed.end !== "0000-00-00" && parsed.end !== "0000-00-00 00:00:00";
    const isCurrent = parsed.current === "1";

    return (
        <div className={styles.recordTag}>
            <span className={styles.tagDesignation}>{parsed.designation}</span>
            <span className={styles.tagSalon}>{parsed.salon}</span>
            <span className={styles.tagDates}>
                <Calendar size={10} />
                {parsed.start}
                <span className={styles.dateSep}>→</span>
                {isCurrent ? (
                    <span className={styles.currentBadge}>present</span>
                ) : isValid ? (
                    parsed.end
                ) : (
                    <span className={styles.nullDate}>—</span>
                )}
            </span>
        </div>
    );
}

function CountedRow({ item }) {
    const [open, setOpen] = useState(false);
    return (
        <div className={styles.countedRow}>
            <div className={styles.countedRowHeader} onClick={() => setOpen((o) => !o)}>
                <span className={styles.chevron}>
                    {open ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                </span>
                <span className={styles.countedRange}>{item.range}</span>
                <span className={styles.countedMonths}>
                    <Clock size={10} />
                    {item.months} mo
                </span>
                <span className={styles.countedSources}>{item.sources.length} source{item.sources.length !== 1 ? "s" : ""}</span>
            </div>
            {open && (
                <div className={styles.countedSub}>
                    {item.sources.map((src, i) => (
                        <RecordTag key={i} record={src} />
                    ))}
                </div>
            )}
        </div>
    );
}

function SectionBlock({ sectionMeta, items }) {
    const [open, setOpen] = useState(true);
    const Icon = sectionMeta.icon;

    if (!items || items.length === 0) return null;

    return (
        <div className={`${styles.section} ${styles[`section_${sectionMeta.key}`]}`}>
            <div className={styles.sectionHeader} onClick={() => setOpen((o) => !o)}>
                <span className={styles.sectionIconWrap}>
                    <Icon size={12} />
                </span>
                <span className={styles.sectionLabel}>{sectionMeta.label}</span>
                <span className={styles.sectionCount}>{items.length}</span>
                <span className={styles.sectionDesc}>{sectionMeta.description}</span>
                <span className={styles.sectionChevron}>
                    {open ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                </span>
            </div>

            {open && (
                <div className={styles.sectionBody}>
                    {sectionMeta.key === "counted"
                        ? items.map((item, i) => <CountedRow key={i} item={item} />)
                        : items.map((item, i) => (
                            <div key={i} className={styles.logRow}>
                                <span className={styles.logReason}>{item.reason}</span>
                                <RecordTag record={item.record} />
                            </div>
                        ))}
                </div>
            )}
        </div>
    );
}

export default function RbExperienceAnalyser({ data, onClose }) {
    if (!data) return null;
    const { summary, counted, corrected, skipped, corrupted } = data;

    const totalRecords =
        (counted?.length || 0) +
        (corrected?.length || 0) +
        (skipped?.length || 0) +
        (corrupted?.length || 0);

    return (
        <div className={styles.overlay}>
            <div className={styles.contentWrapper} >
                <div className={styles.root}>
                    {/* Header */}
                    <div className={styles.header}>
                        <div className={styles.headerLeft}>
                            <TrendingUp size={13} />
                            <span className={styles.headerTitle}>Experience Analyser</span>
                        </div>
                        <span className={styles.headerMeta}>{totalRecords} records processed</span>

                        <div>
                            <button className={styles.closeBtn} onClick={onClose} aria-label="Close analyser">Close</button>
                        </div>
                    </div>

                    {/* Summary strip */}
                    <div className={styles.summaryStrip}>
                        <div className={styles.summaryMain}>
                            <span className={styles.summaryLabel}>Total Experience</span>
                            <span className={styles.summaryValue}>{summary.label}</span>
                            <span className={styles.summaryMonths}>{summary.totalMonths} months</span>
                        </div>

                        <div className={styles.summaryStats}>
                            {SECTIONS.map((s) => {
                                const count = data[s.key]?.length || 0;
                                const Icon = s.icon;
                                return (
                                    <div key={s.key} className={`${styles.statPill} ${styles[`pill_${s.key}`]}`}>
                                        <Icon size={10} />
                                        <span>{count}</span>
                                        <span className={styles.statLabel}>{s.label}</span>
                                    </div>
                                );
                            })}
                        </div>

                        <div className={styles.summaryMeta}>
                            <span className={styles.summaryMetaItem}>
                                <Layers size={10} />
                                {counted?.reduce((a, c) => a + c.sources.length, 0)} raw → {counted?.length} merged range{counted?.length !== 1 ? "s" : ""}
                            </span>
                        </div>
                    </div>

                    {/* Sections */}
                    <div className={styles.sections}>
                        {SECTIONS.map((s) => (
                            <SectionBlock key={s.key} sectionMeta={s} items={data[s.key]} />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}